import {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useCreateSubCategory} from '@entities/category';
import {CardBox} from '@shared/components';
import {ROUTE__SUB_CATEGORIES} from '@shared/constants/routes';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {FormRow, Header} from '@widgets/common';
import {FormFields} from './FormFields';
import {CreateFormValues, createV8nSchema} from './v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

export const CreateSubCategoryPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {categoryId} = useParams<{categoryId: string}>();
  const {mutateAsync: createSubCategory, isPending} = useCreateSubCategory();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    createEmptyTranslationDict,
  );

  const formMethods = useForm<CreateFormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(createV8nSchema),
    defaultValues: {
      slug: '',
      name: '',
      geos: [],
    },
  });
  const {
    handleSubmit,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: CreateFormValues): Promise<void> => {
    if (!categoryId) return;

    try {
      await createSubCategory({
        categorySlug: categoryId,
        slug: values.slug,
        nameTrans: {
          ...nameTranslations,
          en: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        geos: values.geos.map(g => g.id),
      });
      enqueueSnackbar('Sub-category created', {variant: 'success'});
      navigate(ROUTE__SUB_CATEGORIES.replace(':categoryId', categoryId));
    } catch {
      enqueueSnackbar('Failed to create sub-category', {variant: 'error'});
    }
  };

  return (
    <>
      <Header title="Create sub-category" mb={1} />

      <CardBox bgcolor={COLOR__WHITE} maxWidth={450}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider {...formMethods}>
            <FormFields
              showSlug
              nameTranslations={nameTranslations}
              onNameTranslationsChange={setNameTranslations}
            />
          </FormProvider>

          <FormRow width={420} mt={3}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>

            <Button type="submit" disabled={isPending} sx={{flex: 2}}>
              Create sub-category
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
